import i18n from '../../../i18n';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import httpService from "../../../services/http.service";

const emptyForm = () => ({
  provider_name: "",
  provider_tag: "",
  provider_logo: "",
  description: "",
  api_config_text: "",
  priority: 0,
  status: true,
});

const PaymentProvidersList = () => {
    const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpService.get("/payment-providers");
      const list = res.data?.data;
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e?.response?.data || e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      provider_name: p.provider_name || "",
      provider_tag: p.provider_tag || "",
      provider_logo: p.provider_logo || "",
      description: p.description || "",
      api_config_text:
        p.api_config && typeof p.api_config === "object"
          ? JSON.stringify(p.api_config, null, 2)
          : "",
      priority: p.priority ?? 0,
      status: p.status !== false,
    });
    setFormError("");
    setModalOpen(true);
  };

  const parseApiConfig = () => {
    const t = (form.api_config_text || "").trim();
    if (!t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return undefined;
    }
  };

  const submit = async () => {
    setFormError("");
    if (!form.provider_name.trim()) {
      setFormError(i18n.t("Le nom du provider est obligatoire."));
      return;
    }
    if (!form.provider_tag.trim()) {
      setFormError(i18n.t("Le tag (référence technique) est obligatoire."));
      return;
    }
    const apiConfig = parseApiConfig();
    if (apiConfig === undefined) {
      setFormError(i18n.t("Configuration API : JSON invalide (ou laissez vide)."));
      return;
    }

    const payload = {
      provider_name: form.provider_name.trim(),
      provider_tag: form.provider_tag.trim(),
      priority: parseInt(form.priority, 10) || 0,
      status: form.status,
      provider_logo: form.provider_logo.trim() || null,
      description: form.description.trim() || null,
      api_config: apiConfig,
    };

    setSaving(true);
    try {
      if (editingId) {
        await httpService.put(`/payment-providers/${editingId}`, payload);
      } else {
        await httpService.post("/payment-providers", payload);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.errors ||
        i18n.t("Enregistrement impossible.");
      setFormError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await httpService.delete(`/payment-providers/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.message || i18n.t("Suppression impossible."));
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <AppBreadcrumb title={t("Providers de paiement")} />
        <FecthingSpinner />
      </div>
    );
  }

  return (
    <div className="container-fluid payment-providers-page">
      <AppBreadcrumb title={t("Providers de paiement")}>
        <div className="col-md-6 col-sm-12 text-md-right">
          <Link
            to="/payment-settings"
            className="btn btn-sm btn-outline-secondary"
          >
            <i className="icon-arrow-left mr-1" aria-hidden />
            {t("Paramètres de paiement")}
          </Link>
        </div>
      </AppBreadcrumb>

      <div
        className="card border-0 shadow-sm mb-3"
        style={{ borderRadius: "12px" }}
      >
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between py-3">
          <p className="text-muted small mb-0 mr-3">
            {t("Référentiel des partenaires techniques (routing, frais, canaux).")}
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ borderRadius: "8px" }}
            onClick={openCreate}
          >
            <i className="icon-plus mr-1" aria-hidden />
            {t("Nouveau provider")}
          </button>
        </div>
      </div>

      <div
        className="card border-0 shadow-sm"
        style={{ borderRadius: "12px" }}
      >
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: "0.92rem" }}>
              <thead>
                <tr style={{ background: "#f4f6f9", color: "#495057" }}>
                  <th className="border-0 pl-4 py-3">{t("Nom")}</th>
                  <th className="border-0 py-3">{t("Tag")}</th>
                  <th className="border-0 py-3">{t("Priorité")}</th>
                  <th className="border-0 py-3">{t("Statut")}</th>
                  <th className="border-0 py-3">{t("Description")}</th>
                  <th
                    className="border-0 pr-4 py-3 text-right"
                    style={{ width: 96 }}
                  >
                    <span className="sr-only">{t("Actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="pl-4 align-middle font-weight-medium">
                      {p.provider_name}
                    </td>
                    <td className="align-middle">
                      <code className="small">{p.provider_tag}</code>
                    </td>
                    <td className="align-middle">{p.priority ?? 0}</td>
                    <td className="align-middle">
                      <span
                        className={`badge ${
                          p.status ? "badge-success" : "badge-secondary"
                        }`}
                      >
                        {p.status ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="align-middle text-muted small">
                      {p.description
                        ? p.description.length > 80
                          ? `${p.description.slice(0, 80)}…`
                          : p.description
                        : "—"}
                    </td>
                    <td className="pr-4 align-middle text-right">
                      <div
                        className="d-inline-flex align-items-center justify-content-end"
                        style={{ gap: "6px" }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center p-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "8px",
                          }}
                          title={t("Modifier")}
                          aria-label={i18n.t("Modifier ce provider")}
                          onClick={() => openEdit(p)}
                        >
                          <i className="icon-pencil" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center p-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "8px",
                          }}
                          title={t("Supprimer")}
                          aria-label={i18n.t("Supprimer ce provider")}
                          onClick={() => setDeleteTarget(p)}
                        >
                          <i className="icon-trash" aria-hidden />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      {t("Aucun provider. Cliquez sur « Nouveau provider ».")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: "rgba(15, 23, 42, 0.45)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: "14px", overflow: "hidden" }}
            >
              <div
                className="modal-header border-0 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                }}
              >
                <h5 className="modal-title mb-0">
                  {editingId ? i18n.t("Modifier le provider") : i18n.t("Nouveau provider")}
                </h5>
                <button
                  type="button"
                  className="close text-white"
                  onClick={() => !saving && setModalOpen(false)}
                  disabled={saving}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div
                className="modal-body"
                style={{
                  maxHeight: "72vh",
                  overflowY: "auto",
                  background: "#fafbfc",
                }}
              >
                {formError && (
                  <div className="alert alert-danger border-0 shadow-sm mb-3">
                    {formError}
                  </div>
                )}
                <div className="form-group">
                  <label className="small font-weight-medium text-secondary">
                    {t("Nom affiché")}
                  </label>
                  <input
                    type="text"
                    className="form-control border-0 shadow-sm"
                    style={{ borderRadius: "8px" }}
                    value={form.provider_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, provider_name: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label className="small font-weight-medium text-secondary">
                    {t("Tag technique (unique)")}
                  </label>
                  <input
                    type="text"
                    className="form-control border-0 shadow-sm text-uppercase"
                    style={{ borderRadius: "8px" }}
                    placeholder={t("EX: FLUTTERWAVE")}
                    value={form.provider_tag}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, provider_tag: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>
                <div className="row">
                  <div className="col-md-6 form-group">
                    <label className="small font-weight-medium text-secondary">
                      {t("Priorité")}
                    </label>
                    <input
                      type="number"
                      min={0}
                      className="form-control border-0 shadow-sm"
                      style={{ borderRadius: "8px" }}
                      value={form.priority}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          priority: e.target.value,
                        }))
                      }
                      disabled={saving}
                    />
                  </div>
                  <div className="col-md-6 form-group d-flex align-items-end">
                    <label className="d-flex align-items-center mb-3">
                      <input
                        type="checkbox"
                        checked={form.status}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, status: e.target.checked }))
                        }
                        disabled={saving}
                      />
                      <span className="ml-2 text-secondary">{t("Actif")}</span>
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label className="small font-weight-medium text-secondary">
                    {t("Logo (chemin / URL stockage, optionnel)")}
                  </label>
                  <input
                    type="text"
                    className="form-control border-0 shadow-sm"
                    style={{ borderRadius: "8px" }}
                    value={form.provider_logo}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, provider_logo: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label className="small font-weight-medium text-secondary">
                    {t("Description")}
                  </label>
                  <textarea
                    className="form-control border-0 shadow-sm"
                    style={{ borderRadius: "8px" }}
                    rows={2}
                    value={form.description}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="small font-weight-medium text-secondary">
                    {t("Configuration API (JSON optionnel)")}
                  </label>
                  <textarea
                    className="form-control border-0 shadow-sm font-monospace small"
                    style={{ borderRadius: "8px" }}
                    rows={6}
                    placeholder='{"clé": "valeur"}'
                    value={form.api_config_text}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        api_config_text: e.target.value,
                      }))
                    }
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="modal-footer border-0 bg-white">
                <button
                  type="button"
                  className="btn btn-light border"
                  style={{ borderRadius: "8px" }}
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn text-white border-0"
                  style={{
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                  }}
                  onClick={submit}
                  disabled={saving}
                >
                  {saving ? "Enregistrement…" : i18n.t("Enregistrer")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: "rgba(15, 23, 42, 0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: "14px", overflow: "hidden" }}
            >
              <div
                className="modal-header border-0 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                }}
              >
                <div>
                  <h5 className="modal-title mb-1">{t("Suppression du provider")}</h5>
                  <p className="mb-0 small font-weight-normal opacity-75">
                    {t("Les frais et liaisons canaux associés peuvent être impactés")}
                  </p>
                </div>
                <button
                  type="button"
                  className="close text-white"
                  onClick={() => !deleteLoading && setDeleteTarget(null)}
                  disabled={deleteLoading}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ background: "#fafbfc" }}>
                <p className="text-dark mb-2">
                  Confirmer la suppression de{" "}
                  <strong>{deleteTarget.provider_name}</strong> (
                  <code>{deleteTarget.provider_tag}</code>) ?
                </p>
                <p className="text-muted small mb-0">
                  {t("En base, les enregistrements liés (frais, canaux provider) peuvent être supprimés en cascade selon la configuration SQL.")}
                </p>
              </div>
              <div className="modal-footer border-0 bg-light">
                <button
                  type="button"
                  className="btn btn-light border"
                  style={{ borderRadius: "8px" }}
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleteLoading}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{ borderRadius: "8px" }}
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Suppression…" : i18n.t("Supprimer")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentProvidersList;
